/**
 * Shared market-aware formatting (US port Phase 2).
 *
 * The active market profile is served by /api/market-profile (single source
 * of truth — see backend/app/core/market_profile.py). Until the fetch
 * resolves, India (nifty_kite) defaults apply so nothing renders wrong
 * during login/boot under the India profile.
 *
 * ALL currency/number rendering should go through these helpers — never
 * hardcode '₹', '$', or a locale in a page again.
 */

export interface MarketProfilePublic {
  key: string;
  display_name: string;
  provider: string;
  underlying_name: string;
  currency_symbol: string;
  currency_code: string;
  locale: string;
  strike_step: number;
  lot_size: number;
  strike_window: number;
  expiry_window: number | null;
  timezone_name: string;
  session_open: string;
  session_close: string;
  // The climb engine's own gate (buffered inside the session, weekdays only).
  // Outside it `_run_climbing_cycles` returns immediately — no crawler climbs.
  engine_open: string;
  engine_close: string;
  expiry_time: string;
  trading_hours_per_day: number;
  risk_free_rate: number;
  serial_prefix: string;
  // Global crawler-form defaults — ONE profile value drives every page
  // (Add Crawler/Leverage Buy modals, trigger pages, calculator).
  default_strike_diff: number;   // buy↔sell gap in strike STEPS
  default_premium_value: number; // climb-up PV default (local currency)
  // Half-width (points) of the wing-minima anchor window around protective
  // legs — mirrors ANCHOR_WINDOW in backend payoff_calculator.py
  anchor_window: number;
  // Crawler-algo climb guards — mirror the module constants in backend
  // crawler_execution_service.py so the Crawler Algo page can show the SAME
  // scan the engine runs (how far it looks, and why it is holding).
  max_climb_skip_steps: number;      // farthest-first scan span, in strike steps
  skip_strike_quote_spread: number;  // buy-leg book width above which a strike is "dead"
  max_quote_spread_abs: number;      // quote-sanity: absolute ask−bid ceiling
  max_quote_spread_pct: number;      // quote-sanity: ask−bid as a fraction of bid
  [k: string]: unknown;
}

const INDIA_DEFAULTS: MarketProfilePublic = {
  key: 'nifty_kite',
  display_name: 'NIFTY (Zerodha Kite)',
  provider: 'kite',
  underlying_name: 'NIFTY',
  currency_symbol: '₹',
  currency_code: 'INR',
  locale: 'en-IN',
  strike_step: 50,
  lot_size: 65,
  strike_window: 25,
  expiry_window: 4,
  timezone_name: 'Asia/Kolkata',
  session_open: '09:15',
  session_close: '15:30',
  engine_open: '09:18',
  engine_close: '15:29',
  expiry_time: '15:30',
  trading_hours_per_day: 6.25,
  risk_free_rate: 0.09,
  serial_prefix: 'NG',
  default_strike_diff: 10,
  default_premium_value: 1.0,
  anchor_window: 200,
  max_climb_skip_steps: 4,
  skip_strike_quote_spread: 4,
  max_quote_spread_abs: 5,
  max_quote_spread_pct: 0.5,
};

// Last successfully-loaded profile survives reloads/offline so the UI never
// falls back to the wrong market's chips (e.g. NIFTY header on a US deploy)
// while the backend is unreachable. Non-secret config — safe to persist.
const PROFILE_CACHE_KEY = 'market_profile_cache';

function cachedProfile(): MarketProfilePublic | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.key ? { ...INDIA_DEFAULTS, ...parsed } : null;
  } catch {
    return null;
  }
}

let profile: MarketProfilePublic = cachedProfile() ?? INDIA_DEFAULTS;
const listeners = new Set<(p: MarketProfilePublic) => void>();
let retryTimer: ReturnType<typeof setTimeout> | null = null;

export function getMarketProfile(): MarketProfilePublic {
  return profile;
}

/** Subscribe to profile changes (fires once after load). Returns unsubscribe. */
export function onMarketProfile(cb: (p: MarketProfilePublic) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Fetch the active profile from the backend. Call once after authentication
 * (App.tsx). Safe to call repeatedly. On failure the current value (cached
 * profile from a previous session, else defaults) is kept and the fetch
 * retries every 10s until it succeeds — so a backend restart or brief
 * offline spell never leaves the UI stuck on the wrong market.
 */
export async function loadMarketProfile(): Promise<MarketProfilePublic> {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  try {
    const r = await fetch('/api/market-profile', {
      headers: { Authorization: `Bearer ${localStorage.getItem('platform_token')}` },
    });
    if (r.ok) {
      profile = { ...INDIA_DEFAULTS, ...(await r.json()) };
      try {
        localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
      } catch { /* storage full/blocked — cache is best-effort */ }
      listeners.forEach((cb) => cb(profile));
      return profile;
    }
  } catch {
    /* fall through to retry */
  }
  retryTimer = setTimeout(() => { loadMarketProfile(); }, 10_000);
  return profile;
}

/** The active currency symbol ('₹' / '$'). */
export function currencySymbol(): string {
  return profile.currency_symbol;
}

/**
 * Display name of the active underlying for UI labels:
 * 'NIFTY' under nifty_kite, 'SPXW' under spx_tastytrade.
 *
 * Owner 2026-09-15: every page labels the US contracts SPXW (the PM-settled
 * weeklies we actually trade), not SPX — driven by the profile's
 * `underlying_name` so the label and the contract root can never disagree.
 */
export function underlyingLabel(): string {
  if (profile.underlying_name) return profile.underlying_name;
  return profile.provider === 'kite' ? 'NIFTY' : 'SPXW';
}

export interface FormatCurrencyOptions {
  /** fraction digits (default 2) */
  decimals?: number;
  /** min fraction digits; defaults to `decimals` */
  minDecimals?: number;
  /** prepend '+' for positive values (default false) */
  sign?: boolean;
  /** omit the currency symbol (default false) */
  bare?: boolean;
}

/**
 * Format a currency amount in the active market's symbol + locale.
 * e.g. India: ₹1,23,456.78 — US: $123,456.78
 */
export function formatCurrency(value: number | null | undefined, opts: FormatCurrencyOptions = {}): string {
  const { decimals = 2, minDecimals = opts.decimals ?? 2, sign = false, bare = false } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) {
    return bare ? '—' : `${profile.currency_symbol}—`;
  }
  const abs = Math.abs(value).toLocaleString(profile.locale, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: decimals,
  });
  const prefix = value < 0 ? '-' : sign && value > 0 ? '+' : '';
  return bare ? `${prefix}${abs}` : `${prefix}${profile.currency_symbol}${abs}`;
}

/** Locale-aware plain number formatting (replaces toLocaleString('en-IN')). */
export function formatNumber(value: number | null | undefined, decimals = 2, minDecimals?: number): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString(profile.locale, {
    minimumFractionDigits: minDecimals ?? decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Weekday abbreviation ("Mon", "Tue", ...) for an ISO expiry date
 * (YYYY-MM-DD). Parses the calendar parts directly so the result never
 * shifts a day when the browser runs in a negative-UTC-offset timezone
 * (which `new Date('YYYY-MM-DD')` would do). Returns '' on bad input.
 */
export function expiryWeekday(expiry: string | null | undefined): string {
  if (!expiry) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiry);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return '';
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
}

/* ── CC / LC crawler types and premium sacrifice (D8, D12, D18) ─────────── */

export type CrawlerKind = 'cc' | 'lc';

/**
 * The type of a crawler row. Rows written before the CC/LC split carry no
 * type, and every one of them is a crawler crawler — that is what the
 * platform ran until now — so an absent value reads 'cc' rather than unknown.
 */
export function crawlerKind(row: { crawler_type?: string | null } | null | undefined): CrawlerKind {
  return row?.crawler_type === 'lc' ? 'lc' : 'cc';
}

/** Tailwind classes for the CC / LC badge — LC borrows the leverage-buy green. */
export function crawlerKindClass(kind: CrawlerKind): string {
  return kind === 'lc'
    ? 'bg-success-500/15 text-success-400 border border-success-500/25'
    : 'bg-warning-500/15 text-warning-400 border border-warning-500/25';
}

/**
 * "0.20" when both sides share an allowance, "C 0.20 · P 0.30" when they
 * differ. PS is per side (D12/D18); `premium_value` is the pre-split single
 * value and stands in for a row that has not been migrated.
 */
export function formatPs(
  row: { ps_call?: number | null; ps_put?: number | null; premium_value?: number | null } | null | undefined,
): string {
  if (!row) return '—';
  const call = row.ps_call ?? row.premium_value;
  const put = row.ps_put ?? row.premium_value;
  if (call === null || call === undefined) return '—';
  if (put === null || put === undefined || call === put) return String(call);
  return `C ${call} · P ${put}`;
}

/**
 * Crawler Calculator URL pre-filled with one crawler's expiries and per-side
 * gaps (owner 2026-09-18), so the ladder opens priced exactly as the crawler
 * is set. The buy expiry is the call side's effective one (an Expiry Transfer
 * override, else the base) — the calculator has a single BE box.
 */
export function crawlerCalculatorHref(
  row: {
    buy_expiry: string; buy_expiry_call?: string | null; sell_expiry: string;
    gap_call_steps?: number | null; gap_put_steps?: number | null; strike_diff?: number | null;
  },
): string {
  const q = new URLSearchParams();
  q.set('be', row.buy_expiry_call || row.buy_expiry);
  q.set('se', row.sell_expiry);
  const call = row.gap_call_steps ?? row.strike_diff;
  const put = row.gap_put_steps ?? row.strike_diff;
  if (call !== null && call !== undefined) q.set('sd_call', String(call));
  if (put !== null && put !== undefined) q.set('sd_put', String(put));
  return `/crawler-calculator?${q.toString()}`;
}

/** Same shape for the per-side strike gap: "18" when equal, "C 10 · P 26" when not. */
export function formatGapSteps(
  row: { gap_call_steps?: number | null; gap_put_steps?: number | null; strike_diff?: number | null } | null | undefined,
): string {
  if (!row) return '—';
  const call = row.gap_call_steps ?? row.strike_diff;
  const put = row.gap_put_steps ?? row.strike_diff;
  if (call === null || call === undefined) return '—';
  if (put === null || put === undefined || call === put) return String(call);
  return `C ${call} · P ${put}`;
}

/* ── Leg nomenclature (owner 2026-10-09) ──────────────────────────────────
 * One name per unit everywhere on screen:
 *   MS = main sell · MC = main crawler · LC = leverage crawler · LB = leverage buy
 * followed by the side (CE / PE) and the pair. Display only — the stored tags
 * (main-sell-CE-1, CC / PC, LBC / LBP) and crawler_type ('cc' / 'lc') are unchanged. */

export type LegUnit = 'MS' | 'MC' | 'LC' | 'LB';

export const LEG_UNIT_NAMES: Record<LegUnit, string> = {
  MS: 'Main sell',
  MC: 'Main crawler',
  LC: 'Leverage crawler',
  LB: 'Leverage buy',
};

/** 'MC' / 'LC' for a crawler row — the badge text for crawlerKind(). */
export function crawlerKindLabel(kind: CrawlerKind): 'MC' | 'LC' {
  return kind === 'lc' ? 'LC' : 'MC';
}

/**
 * Unit, side and pair of a position's strategy tag, e.g. "MS CE·P1",
 * "MC PE·P2", "LB CE". `crawlerType` tells an LC leg from an MC leg — both
 * carry CC / PC — and defaults to MC. Null for a tag outside the four units.
 */
export function legName(
  tag: string | null | undefined,
  crawlerType?: string | null,
): { unit: LegUnit; side: 'CE' | 'PE'; pair: string | null; label: string; title: string } | null {
  if (!tag) return null;
  let unit: LegUnit;
  let side: 'CE' | 'PE';
  let pair: string | null = null;
  const ms = /^main-sell-(CE|PE)(?:-(\d+))?$/.exec(tag);
  // "-?" = a unit not in a pair yet (the trade card's enriched tag): MC / LC / LB without a pair number
  const unitTag = /^(CC|PC|LBC|LBP)(?:-(\w+|\?))?$/.exec(tag);
  if (ms) {
    unit = 'MS';
    side = ms[1] as 'CE' | 'PE';
    pair = ms[2] ?? null;
  } else if (unitTag) {
    const t = unitTag[1];
    unit = t.startsWith('LB') ? 'LB' : crawlerType === 'lc' ? 'LC' : 'MC';
    side = t === 'CC' || t === 'LBC' ? 'CE' : 'PE';
    pair = unitTag[2] && unitTag[2] !== 'undefined' && unitTag[2] !== '?' ? unitTag[2] : null;
  } else {
    return null;
  }
  const label = `${unit} ${side}${pair ? `·P${pair}` : ''}`;
  const title = `${LEG_UNIT_NAMES[unit]} — ${side === 'CE' ? 'call' : 'put'}${pair ? `, pair ${pair}` : ''}`;
  return { unit, side, pair, label, title };
}
